import { createLogger, LogLevel } from './logger';
import { getYmGoal } from '../../../../pages/catalog/assets/js/utils/helpYmGoals';
import { sendElementYmGoal } from '../../metrika/actions';

const logger = createLogger('TariffComparison', LogLevel.INFO);

/**
 * Класс для управления сравнением тарифов
 */
export class TariffComparison {
  private isProcessingToggle: boolean = false;
  private compareButton: HTMLElement | null = null;
  private itemsBlock: HTMLElement | null = null;
  private compareBlock: HTMLElement | null = null;
  private accordionRows: NodeListOf<HTMLElement> | null = null;
  private priceSwitcher: HTMLElement | null = null;
  private modalContent: HTMLElement;
  private originalButtonText: string = '';

  /**
   * Конструктор
   * @param modalContent Содержимое модального окна
   */
  constructor(modalContent: HTMLElement) {
    this.modalContent = modalContent;
    this.initialize();
  }

  /**
   * Инициализация компонента сравнения тарифов
   */
  private initialize(): void {
    logger.info('Инициализация компонента сравнения тарифов');
    
    this.compareButton = this.modalContent.querySelector('#tarif-compare');
    this.itemsBlock = this.modalContent.querySelector('.tariff__items');
    this.compareBlock = this.modalContent.querySelector('.tariff__compare');
    this.priceSwitcher = this.modalContent.querySelector('.header__price__switcher');

    // При инициализации скрываем блок сравнения
    if (this.compareBlock) {
      this.compareBlock.style.display = 'none';
    }

    // Сохраняем оригинальный текст кнопки
    if (this.compareButton) {
      this.originalButtonText = this.compareButton.textContent || 'Сравнить тарифы';
      this.compareButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggle();
      });
      logger.info('Обработчик кнопки сравнения добавлен');
    }
    
    // Инициализация аккордеона
    this.initAccordion();
  }
  
  /**
   * Инициализирует функционал аккордеона для строк сравнения
   */
  private initAccordion(): void {
    // Находим все строки сравнения
    this.accordionRows = this.modalContent.querySelectorAll('.compare__content__row');
    
    if (!this.accordionRows || this.accordionRows.length === 0) {
      logger.warn('Строки сравнения не найдены');
      return;
    }
    
    logger.info('Инициализация аккордеона', this.accordionRows.length, 'строк');
    
    // Добавляем обработчики для каждой строки
    this.accordionRows.forEach((row, index) => {
      const opener = row.querySelector('.compare__opener');
      const main = row.querySelector('.compare__main');
      const description = row.querySelector('.compare__description');
      
      if (main && description) {
        main.addEventListener('click', () => {
          logger.info('Клик по строке аккордеона', index);
          // Переключаем класс active для иконки и для описания
          if (opener) {
            opener.classList.toggle('active');
          }
          description.classList.toggle('active');
        });
      }
    });
  }

  /**
   * Переключает отображение между блоками тарифов и их сравнением
   */
  public toggle(): void {
    // Защита от частых кликов
    if (this.isProcessingToggle) {
      logger.warn('Переключение уже в процессе');
      return;
    }
    
    this.isProcessingToggle = true;
    
    if (!this.compareButton || !this.itemsBlock || !this.compareBlock) {
      logger.error('Не найдены необходимые элементы для переключения');
      this.isProcessingToggle = false;
      return;
    }
    
    // Отправляем цель для ТЕКУЩЕГО состояния кнопки (до изменения)
    if (this.compareButton.getAttribute('data-ym-goal')) {
      sendElementYmGoal(this.compareButton);
    }
    
    const isCompareVisible = this.compareBlock.style.display !== 'none';
    
    logger.info('Переключение сравнения тарифов', { isCompareVisible });
    
    if (isCompareVisible) {
      // Скрываем сравнение, показываем тарифы
      this.compareBlock.style.display = 'none';
      this.itemsBlock.style.display = '';
      this.compareButton.textContent = this.originalButtonText;
      
      // data-ym-goal для сравнения (следующее действие)
      const ymGoalCompare = getYmGoal('compare');
      if (ymGoalCompare) {
        this.compareButton.setAttribute('data-ym-goal', ymGoalCompare);
        // Сбрасываем флаг отправленной цели при изменении data-ym-goal
        this.compareButton.removeAttribute('data-ym-goal-sent');
      } else {
        this.compareButton.removeAttribute('data-ym-goal');
      }
      
      // Показываем переключатель цен (убираем класс hidden)
      if (this.priceSwitcher) {
        this.priceSwitcher.classList.remove('hidden');
      }
      
      logger.info('Показаны тарифы, скрыто сравнение');
    } else {
      // Показываем сравнение, скрываем тарифы
      this.compareBlock.style.display = '';
      this.itemsBlock.style.display = 'none';
      this.compareButton.textContent = 'Скрыть';
      
      // data-ym-goal для скрытия (следующее действие)
      const ymGoalHide = getYmGoal('hide');
      if (ymGoalHide) {
        this.compareButton.setAttribute('data-ym-goal', ymGoalHide);
        // Сбрасываем флаг отправленной цели при изменении data-ym-goal
        this.compareButton.removeAttribute('data-ym-goal-sent');
      } else {
        this.compareButton.removeAttribute('data-ym-goal');
      }
      
      // Скрываем переключатель цен (добавляем класс hidden)
      if (this.priceSwitcher) {
        this.priceSwitcher.classList.add('hidden');
      }
      
      logger.info('Показано сравнение, скрыты тарифы');
    }
    
    // Сбрасываем флаг обработки через небольшую задержку
    setTimeout(() => {
      this.isProcessingToggle = false;
    }, 300);
  }

  /**
   * Принудительно показывает тарифы
   */
  public showTariffs(): void {
    if (this.compareBlock && this.itemsBlock && this.compareButton) {
      this.compareBlock.style.display = 'none';
      this.itemsBlock.style.display = '';
      this.compareButton.textContent = this.originalButtonText;
      
      if (this.priceSwitcher) {
        this.priceSwitcher.classList.remove('hidden');
      }
      
      logger.info('Принудительно показаны тарифы');
    }
  }

  /**
   * Принудительно показывает сравнение
   */
  public showComparison(): void {
    if (this.compareBlock && this.itemsBlock && this.compareButton) {
      this.compareBlock.style.display = '';
      this.itemsBlock.style.display = 'none';
      this.compareButton.textContent = 'Скрыть';
      
      if (this.priceSwitcher) {
        this.priceSwitcher.classList.add('hidden');
      }
      
      logger.info('Принудительно показано сравнение');
    }
  }
} 